<template>
  <!-- First-visit explainer modal -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="showIntro" class="modal-overlay" @click.self="dismissIntro" role="dialog" aria-modal="true" aria-labelledby="intro-title">
        <div class="modal intro-modal">
          <div class="intro-modal__body">
            <h2 id="intro-title" class="intro-modal__title">Tarski</h2>
            <p class="intro-modal__line">A logic puzzle game.</p>
            <p class="intro-modal__line">You'll see a world of colored geometric shapes and a predicate logic sentence. Decide: is the sentence <strong>true</strong> or <strong>false</strong> for that world?</p>
            <p class="intro-modal__line">Named after logician Alfred Tarski.</p>
            <p class="intro-modal__line intro-modal__learn">
              <router-link to="/tutorial" @click="dismissIntro">Learn the basics</router-link>
            </p>
            <button class="black-button intro-modal__cta" @click="dismissIntro" autofocus>Play</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Main screen -->
  <div class="home-screen" :class="{ 'home-screen--dimmed': showIntro }">
    <div class="home-center">
      <h1 class="home-title">TARSKI</h1>

      <button class="home-summary" @click="toggleSettings" :aria-expanded="settingsOpen.toString()">
        <span class="home-summary__text">{{ summaryLine }}</span>
        <span class="home-summary__chevron" :class="{ 'home-summary__chevron--open': settingsOpen }" aria-hidden="true">›</span>
      </button>

      <Transition name="settings-reveal">
        <div v-if="settingsOpen" class="home-settings">
          <div class="setting-row">
            <span class="setting-row__label">Complexity</span>
            <div class="seg-group" role="group" :aria-label="'Complexity: ' + levelOption">
              <button
                v-for="opt in levelOptions" :key="opt"
                class="seg-btn" :class="{ 'seg-btn--active': levelOption === opt }"
                @click="levelOption = opt"
              >{{ levelLabels[opt] ?? opt }}</button>
            </div>
          </div>
          <div class="setting-row">
            <span class="setting-row__label">Seconds</span>
            <div class="seg-group" role="group" :aria-label="'Time per challenge: ' + timeOption + ' seconds'">
              <button
                v-for="opt in timeOptions" :key="opt"
                class="seg-btn" :class="{ 'seg-btn--active': timeOption === opt }"
                @click="timeOption = opt"
              >{{ opt }}</button>
            </div>
          </div>
          <div class="setting-row">
            <span class="setting-row__label">Challenges</span>
            <div class="seg-group" role="group" :aria-label="'Number of challenges: ' + amountOption">
              <button
                v-for="opt in amountOptions" :key="opt"
                class="seg-btn" :class="{ 'seg-btn--active': amountOption === opt }"
                @click="amountOption = opt"
              >{{ opt }}</button>
            </div>
          </div>
        </div>
      </Transition>

      <button class="black-button home-play-btn" @click="start">Start</button>

      <p v-if="!isLoggedIn" class="home-guest-notice">
        Playing as guest — using preset challenges.
        <router-link to="/account">Sign in</router-link> to unlock generated challenges and save scores.
      </p>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';

const INTRO_KEY = 'tarski-intro-seen';

const router = useRouter();
const { isLoggedIn } = useAuth();

const showIntro = ref(false);
const settingsOpen = ref(false);

onMounted(() => {
  if (!localStorage.getItem(INTRO_KEY)) {
    showIntro.value = true;
  }
});

function dismissIntro() {
  showIntro.value = false;
  localStorage.setItem(INTRO_KEY, '1');
}

function toggleSettings() {
  settingsOpen.value = !settingsOpen.value;
}

const levelOptions = ['easy', 'moderate', 'hard', 'extreme'];
const levelLabels = { moderate: 'medium' };
const timeOptions = ['80', '40', '20', '10'];
const amountOptions = ['05', '10', '20', '40'];

const levelOption = ref('easy');
const timeOption = ref('80');
const amountOption = ref('05');

const summaryLine = computed(() => {
  const level = levelLabels[levelOption.value] ?? levelOption.value;
  const amount = amountOption.value;
  const time = timeOption.value;
  return `${amount} challenges · ${time}s · ${level.charAt(0).toUpperCase() + level.slice(1)}`;
});

function start() {
  router.push({
    name: 'test',
    query: {
      complexity: levelOption.value,
      time: timeOption.value,
      challengeAmount: amountOption.value,
      challengeType: 'test',
      preset: isLoggedIn.value ? 'false' : 'true',
    },
  });
}
</script>

<style scoped>
.home-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - var(--nav-height));
  padding: 32px 24px;
  box-sizing: border-box;
  transition: opacity 0.2s ease;
}

.home-screen--dimmed {
  opacity: 0.3;
  pointer-events: none;
}

.home-center {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 480px;
}

.home-title {
  font-size: clamp(64px, 14vw, 120px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 0.9;
  color: var(--text-color-bright);
  margin: 0 0 32px;
  font-family: var(--font), monospace;
}

/* Summary toggle */
.home-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  border-bottom: 1px solid transparent;
  color: var(--text-color);
  font-family: var(--font), monospace;
  font-size: 14px;
  letter-spacing: 0.04em;
  padding: 4px 0;
  margin-bottom: 8px;
  text-align: left;
  transition: color 0.15s, border-color 0.15s;
}

.home-summary:hover {
  color: var(--text-color-bright);
  border-bottom-color: var(--border-color-bright);
}

.home-summary:focus-visible {
  outline: 2px solid var(--text-color-bright);
  outline-offset: 3px;
}

.home-summary__chevron {
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s var(--ease-out-quart);
  transform: rotate(0deg);
  display: inline-block;
}

.home-summary__chevron--open {
  transform: rotate(90deg);
}

/* Inline settings panel */
.home-settings {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px 0 20px;
  overflow: hidden;
}

.setting-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-row__label {
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color);
  width: 88px;
  flex-shrink: 0;
}

/* Settings reveal transition */
.settings-reveal-enter-active {
  transition: opacity 0.22s var(--ease-out-quart), transform 0.25s var(--ease-out-expo);
}
.settings-reveal-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.settings-reveal-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}
.settings-reveal-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Play button */
.home-play-btn {
  margin-top: 28px;
  min-width: 140px;
}

/* Guest notice */
.home-guest-notice {
  margin-top: 20px;
  font-size: 13px;
  color: var(--text-color);
  line-height: 1.6;
}

.home-guest-notice a {
  color: var(--text-color-bright);
}

/* Intro modal — overrides .modal base (width and alignment) */
.intro-modal {
  width: min(400px, calc(100vw - 48px));
  padding: 40px 32px 36px;
  text-align: left;
}

.intro-modal__title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-color-bright);
  margin: 0 0 20px;
  font-family: var(--font), monospace;
}

.intro-modal__line {
  font-size: 15px;
  color: var(--text-color);
  line-height: 1.65;
  margin: 0 0 12px;
  max-width: 52ch;
}

.intro-modal__line strong {
  color: var(--text-color-bright);
  font-weight: 600;
}

.intro-modal__learn {
  margin-top: 8px;
}

.intro-modal__learn a {
  font-size: 13px;
  color: var(--text-color);
  text-decoration-line: underline;
  text-decoration-color: var(--border-color);
  text-underline-offset: 3px;
  transition: color 0.15s, text-decoration-color 0.15s;
}

.intro-modal__learn a:hover {
  color: var(--text-color-bright);
  text-decoration-color: var(--text-color-bright);
}

.intro-modal__cta {
  margin-top: 24px;
  min-width: 100%;
}

/* Mobile */
@media screen and (max-width: 639px) {
  .home-screen {
    align-items: flex-start;
    padding: 28px 16px;
    min-height: auto;
  }

  .home-title {
    margin-bottom: 24px;
  }

  .home-play-btn {
    width: 100%;
  }

  .setting-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .setting-row__label {
    width: 100%;
    margin-bottom: 0;
  }

  .seg-group {
    width: 100%;
  }

  .seg-btn {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 4px;
    font-size: 12px;
  }
}

/* Landscape mobile */
@media screen and (max-height: 639px) and (orientation: landscape) {
  .home-screen {
    min-height: 100svh;
    padding: 16px 24px;
  }

  .home-title {
    font-size: clamp(40px, 10vh, 72px);
    margin-bottom: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-summary__chevron {
    transition: none;
  }
}
</style>
